import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  FontSize,
  FontColor,
  Paragraph,
  FontBackgroundColor,
  Link,
  List,
  BlockQuote,
  Table,
  TableToolbar,
  MediaEmbed,
  Alignment,
  RemoveFormat,
  Undo,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import { useEffect, useState } from "react";
import { addSettingsData, getSettingsData } from "../service/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { RefreshCw } from "lucide-react";

const Settings = () => {
  const [formValues, setFormValues] = useState({
    title: "",
    description: "",
    privacy_policy: "",
    app_version: "",
    maintenance: false,
    onlinePayment: false,
  });
  const [editorInstance, setEditorInstance] = useState<any>(null);

  useEffect(() => {
    getSettings();
  }, []);

  useEffect(() => {
    if (editorInstance && formValues.privacy_policy) {
      editorInstance.setData(formValues.privacy_policy);
    }
  }, [editorInstance, formValues.privacy_policy]);

  const getSettings = async () => {
    try {
      let res = await getSettingsData();
      const data = res.data.data[0];
      setFormValues({
        title: data.title || "",
        description: data.description || "",
        privacy_policy: data.privacy_policy || "",
        app_version: data.app_version || "",
        maintenance: data.maintenance || false,
        onlinePayment: data.onlinePayment || false,
      });
    } catch (err) {
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      let body = {
        title: formValues.title,
        description: formValues.description,
        app_version: formValues.app_version,
        maintenance: formValues.maintenance,
        onlinePayment: formValues.onlinePayment,
        privacy_policy: formValues.privacy_policy,
        id: "1",
      };
      await addSettingsData(body);
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Failed to save settings");
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            App metadata, maintenance mode, payments, and legal copy shown in the mobile app.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => getSettings()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* General settings card */}
      <Card>
        <CardContent className="pt-6 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label htmlFor="settings-title">Title</Label>
              <Input
                id="settings-title"
                type="text"
                name="title"
                placeholder="Enter title"
                value={formValues.title}
                onChange={handleInputChange}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="settings-app-version">App version</Label>
              <Input
                id="settings-app-version"
                type="text"
                name="app_version"
                placeholder="e.g. 1.0.0"
                value={formValues.app_version}
                onChange={handleInputChange}
              />
            </div>
            <div className="space-y-1.5 lg:col-span-2">
              <Label htmlFor="settings-description">Description</Label>
              <Textarea
                id="settings-description"
                name="description"
                placeholder="Short app description"
                className="min-h-24"
                value={formValues.description}
                onChange={handleInputChange}
              />
            </div>
            <div className="lg:col-span-2 flex flex-col sm:flex-row sm:flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <Switch
                  id="maintenance-toggle"
                  checked={formValues.maintenance}
                  onCheckedChange={(checked) => setFormValues({ ...formValues, maintenance: checked })}
                />
                <Label htmlFor="maintenance-toggle" className="cursor-pointer">Maintenance mode</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="payment-toggle"
                  checked={formValues.onlinePayment}
                  onCheckedChange={(checked) => setFormValues({ ...formValues, onlinePayment: checked })}
                />
                <Label htmlFor="payment-toggle" className="cursor-pointer">Online payments</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy policy editor */}
      <Card>
        <CardContent className="pt-6">
          <Label className="mb-2 block">Privacy policy</Label>
          <CKEditor
            editor={ClassicEditor}
            config={{
              toolbar: {
                items: [
                  "undo", "redo", "|",
                  "bold", "italic", "|",
                  "fontSize", "fontColor", "fontBackgroundColor", "|",
                  "link", "bulletedList", "numberedList", "|",
                  "blockQuote", "insertTable", "mediaEmbed", "|",
                  "alignment", "|",
                  "removeFormat",
                ],
              },
              plugins: [
                Bold, Italic, Essentials, Paragraph, Undo, FontSize, FontColor,
                FontBackgroundColor, Link, List, BlockQuote, Table, TableToolbar,
                MediaEmbed, Alignment, RemoveFormat,
              ],
            }}
            data={formValues.privacy_policy}
            onReady={(editor) => {
              setEditorInstance(editor);
            }}
            onChange={(event, editor) => {
              const data = editor.getData();
              setFormValues({ ...formValues, privacy_policy: data });
            }}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSubmit}>Save settings</Button>
      </div>
    </div>
  );
};

export default Settings;
